/**
 * Vocabulário da análise por IA — puro, sem React.
 *
 * Veredito, status, ação sugerida e `cited_as` viram texto e cor aqui, e só
 * aqui: a mesa, o dossiê e o painel do evento leem a mesma tabela.
 */
import type { AiFinding, AiFindingStatus } from '../api/aiFindings.ts'

/* ── veredito ────────────────────────────────────────────────────────────── */

export interface VerdictPresentation {
  label: string
  text: string
  border: string
  fill: string
  /** 4 = ataque … 1 = benigno. Ordena o radar. */
  rank: number
}

const VERDICT: Record<string, VerdictPresentation> = {
  attack: { label: 'Ataque', text: 'text-crit', border: 'border-crit', fill: 'bg-crit', rank: 4 },
  suspicious: { label: 'Suspeito', text: 'text-high', border: 'border-high', fill: 'bg-high', rank: 3 },
  inconclusive: {
    label: 'Inconclusivo',
    text: 'text-fg-secondary',
    border: 'border-line-strong',
    fill: 'bg-line-strong',
    rank: 2,
  },
  benign: { label: 'Benigno', text: 'text-ok', border: 'border-ok', fill: 'bg-ok', rank: 1 },
}

export function verdictPresentation(verdict: string | null): VerdictPresentation | null {
  if (verdict === null) return null
  return VERDICT[verdict] ?? { label: verdict, text: 'text-fg-secondary', border: 'border-line-strong', fill: 'bg-line-strong', rank: 0 }
}

export const VERDICT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'attack', label: 'Ataque' },
  { value: 'suspicious', label: 'Suspeito' },
  { value: 'inconclusive', label: 'Inconclusivo' },
  { value: 'benign', label: 'Benigno' },
]

/* ── status ──────────────────────────────────────────────────────────────── */

const STATUS: Record<AiFindingStatus, string> = {
  triage_only: 'Só triagem',
  awaiting_analysis: 'Aguardando IA',
  analyzed: 'Analisado',
  analysis_failed: 'Falha na análise',
  confirmed: 'Incidente confirmado',
  dismissed: 'Descartado',
}

export function statusLabel(status: string): string {
  return STATUS[status as AiFindingStatus] ?? status
}

export const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = (
  Object.keys(STATUS) as AiFindingStatus[]
).map((value) => ({ value, label: STATUS[value] }))

/** Os botões de decisão só valem enquanto ninguém decidiu e a IA não falhou. */
export function canDecide(status: string): boolean {
  return status === 'analyzed' || status === 'triage_only' || status === 'awaiting_analysis'
}

/* ── ação sugerida (só texto: nada dispara daqui) ────────────────────────── */

const ACTION: Record<string, string> = {
  none: 'Nenhuma ação',
  monitor: 'Monitorar',
  collect_artifact: 'Coletar artefato',
  isolate_host: 'Isolar a máquina',
  escalate: 'Escalar',
}

export function recommendedActionLabel(action: string | null): string | null {
  if (action === null) return null
  return ACTION[action] ?? action
}

/* ── cited_as ────────────────────────────────────────────────────────────── */

export interface Citation {
  kind: 'evidence' | 'signal' | 'other'
  label: string
}

export function citation(value: string): Citation {
  if (value === 'evidence') return { kind: 'evidence', label: 'Evidência da IA' }
  if (value.startsWith('signal:')) return { kind: 'signal', label: `Sinal: ${value.slice('signal:'.length)}` }
  return { kind: 'other', label: value }
}

export function isEvidence(citedAs: readonly string[]): boolean {
  return citedAs.includes('evidence')
}

/* ── números ─────────────────────────────────────────────────────────────── */

export function formatConfidence(confidence: number | null): string | null {
  if (confidence === null || !Number.isFinite(confidence)) return null
  return `${Math.round(Math.min(1, Math.max(0, confidence)) * 100)}%`
}

/* ── radar: máquinas × janelas de 15 min ─────────────────────────────────── */

export const SLOT_MS = 15 * 60 * 1000
export const RADAR_SLOTS = 32
export const RADAR_MAX_HOSTS = 12

/**
 * Peso de um achado na célula. Um ataque sempre aparece por cima de um benigno
 * na mesma janela; sem veredito, a falha pesa mais que a triagem pura.
 */
export function findingRank(finding: AiFinding): number {
  const verdict = verdictPresentation(finding.verdict)
  if (verdict !== null) return 10 + verdict.rank
  if (finding.status === 'analysis_failed') return 3
  if (finding.status === 'awaiting_analysis') return 2
  return 1
}

export interface RadarRow {
  host: string
  endpointId: string
  /** Uma célula por janela, da mais antiga para a mais nova. */
  cells: Array<AiFinding | null>
  worst: number
}

export interface Radar {
  /** Início de cada coluna, epoch ms. */
  slots: number[]
  rows: RadarRow[]
  /** Máquinas carregadas além do teto de linhas. */
  hiddenHosts: number
}

/**
 * Monta o radar a partir do que foi carregado. A API não agrega: isto é uma
 * leitura da página em mãos, não da frota inteira.
 */
export function buildRadar(findings: readonly AiFinding[]): Radar {
  if (findings.length === 0) return { slots: [], rows: [], hiddenHosts: 0 }

  let latest = 0
  for (const finding of findings) latest = Math.max(latest, Date.parse(finding.window_end))
  const lastSlot = Math.floor((latest - 1) / SLOT_MS) * SLOT_MS
  const firstSlot = lastSlot - (RADAR_SLOTS - 1) * SLOT_MS
  const slots = Array.from({ length: RADAR_SLOTS }, (_, i) => firstSlot + i * SLOT_MS)

  const byHost = new Map<string, RadarRow>()
  for (const finding of findings) {
    const start = Date.parse(finding.window_start)
    const end = Date.parse(finding.window_end)
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= firstSlot) continue

    let row = byHost.get(finding.endpoint_id)
    if (row === undefined) {
      row = { host: finding.host, endpointId: finding.endpoint_id, cells: slots.map(() => null), worst: 0 }
      byHost.set(finding.endpoint_id, row)
    }

    const rank = findingRank(finding)
    row.worst = Math.max(row.worst, rank)
    // Uma janela pode cobrir mais de uma coluna (o ciclo lê até 1 h quando atrasa).
    const from = Math.max(0, Math.floor((start - firstSlot) / SLOT_MS))
    const to = Math.min(RADAR_SLOTS - 1, Math.floor((Math.max(start, end - 1) - firstSlot) / SLOT_MS))
    for (let i = from; i <= to; i++) {
      const current = row.cells[i] ?? null
      if (current === null || findingRank(current) < rank) row.cells[i] = finding
    }
  }

  const rows = [...byHost.values()].sort((a, b) => b.worst - a.worst || a.host.localeCompare(b.host))
  return {
    slots,
    rows: rows.slice(0, RADAR_MAX_HOSTS),
    hiddenHosts: Math.max(0, rows.length - RADAR_MAX_HOSTS),
  }
}

/* ── período ─────────────────────────────────────────────────────────────── */

export const PERIOD_OPTIONS: ReadonlyArray<{ value: string; label: string; ms: number | null }> = [
  { value: '24h', label: 'Últimas 24 h', ms: 24 * 3600_000 },
  { value: '7d', label: 'Últimos 7 dias', ms: 7 * 24 * 3600_000 },
  { value: '30d', label: 'Últimos 30 dias', ms: 30 * 24 * 3600_000 },
  { value: '', label: 'Todo o período', ms: null },
]

export function periodFrom(value: string, now: number): string | undefined {
  const ms = PERIOD_OPTIONS.find((option) => option.value === value)?.ms ?? null
  return ms === null ? undefined : new Date(now - ms).toISOString()
}
